import DepositView from "issara-sdk/apis/DepositView_apps_BIL";
import MasterAccountList from "issara-sdk/apis/MasterAccountList_apps_BILM";
import MasterEDCList from "issara-sdk/apis/MasterEDCList_apps_BILM";
import WalletPatientGetBalance from "issara-sdk/apis/WalletPatientGetBalance_apps_BIL";
import WalletTransactionGroupCancel from "issara-sdk/apis/WalletTransactionGroupCancel_apps_BILM";
import WalletTransactionGroupList from "issara-sdk/apis/WalletTransactionGroupList_apps_BIL";
import WalletTransactionList from "issara-sdk/apis/WalletTransactionList_apps_BIL";
import WalletTypeList from "issara-sdk/apis/WalletTypeList_apps_BIL";

// serializer
import DepositReceiptSerializer from "issara-sdk/types/DepositReceiptSerializer_apps_BIL";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import type { PayMaster, receiptPaymentsOf } from "../../patient/billingPayModelV4";
import type { WalletGroup, WalletTx, WalletType } from "../../patient/billingWalletModelV4";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  LOAD: "LOAD",
  DEPOSIT_OPEN: "DEPOSIT_OPEN",
  DEPOSIT_SUBMIT: "DEPOSIT_SUBMIT",
  DEPOSIT_CLOSE: "DEPOSIT_CLOSE",
  RECEIPT_PRINT: "RECEIPT_PRINT",
  REFUND: "REFUND",
} as const;

export const SEQUENCE_NAME = "BillingWalletV4" as const;

export const CARD_NAME = "BillingWalletTabV4" as const;

/** =============== Types and Interfaces ===============  */

/** บัญชีรับ / เครื่องรูด ของ pop-up ฝากเงิน */
export type DepositState = {
  accounts: PayMaster[];
  edcs: PayMaster[];
};

/** body ของ BIL/deposit/ ที่มาจาก pop-up (ที่เหลือ sequence เติมเอง) */
export type DepositData = {
  wallet_type: number;
  payments: ReturnType<typeof receiptPaymentsOf>;
  user_remark: string;
};

export type DepositReceipt = DepositReceiptSerializer & { id?: number };

export type RefundData = { username: string; password: string; note: string };

export type State = Partial<{
  BillingWalletV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    patientId: number;
    types: WalletType[];
    groups: WalletGroup[];
    transactions: WalletTx[];
    // ยอดรวมทุก Wallet (wallet-patient-balance)
    total: string;
    deposit: DepositState | null;
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "bilReceiptCodeDetail"
>;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "HandleGetReceiptCode"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.LOAD]: { patientId: number };
  [ACTIONS.DEPOSIT_OPEN]: {};
  [ACTIONS.DEPOSIT_SUBMIT]: {
    data: DepositData;
    onDone?: (receipt: DepositReceipt) => void;
  };
  [ACTIONS.DEPOSIT_CLOSE]: {};
  [ACTIONS.RECEIPT_PRINT]: { receipt: DepositReceipt };
  [ACTIONS.REFUND]: { group: WalletGroup; data: RefundData; onDone?: () => void };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  BillingWalletV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.setState(
    {
      BillingWalletV4Sequence: {
        sequenceIndex: "Action",
        types: [],
        groups: [],
        transactions: [],
        total: "0.00",
        deposit: null,
      },
    },
    () => {
      if ("action" in params) {
        Action(controller, params as unknown as ActionType);
      }
    },
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.LOAD]: HandleLoad,
    [ACTIONS.DEPOSIT_OPEN]: HandleDepositOpen,
    [ACTIONS.DEPOSIT_SUBMIT]: HandleDepositSubmit,
    [ACTIONS.DEPOSIT_CLOSE]: HandleDepositClose,
    [ACTIONS.RECEIPT_PRINT]: HandleReceiptPrint,
    [ACTIONS.REFUND]: HandleRefund,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */

/** ประเภท Wallet, การฝากแต่ละครั้ง, ประวัติรายการ และยอดรวม ของผู้ป่วย */
const HandleLoad: Handler<Params<Actions["LOAD"]>> = async (controller, params) => {
  let state = controller.getState();
  const samePatient = state.BillingWalletV4Sequence?.patientId === params.patientId;
  const extra = { device: controller.data.device, division: controller.data.division };

  controller.setState({
    BillingWalletV4Sequence: {
      ...state.BillingWalletV4Sequence,
      patientId: params.patientId,
      ...(!samePatient && { groups: [], transactions: [], total: "0.00" }),
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.LOAD]: null },
  });

  const results: ApiResult[] = await Promise.all([
    WalletTypeList.list({ apiToken: controller.apiToken, extra }),
    WalletTransactionGroupList.list({
      apiToken: controller.apiToken,
      params: { patient: params.patientId },
      extra,
    }),
    WalletTransactionList.list({
      apiToken: controller.apiToken,
      params: { patient: params.patientId },
      extra,
    }),
    WalletPatientGetBalance.get({
      apiToken: controller.apiToken,
      params: { patient: params.patientId },
      extra,
    }),
  ]);

  const error = results.find((result) => result[1])?.[1];

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.LOAD, error });

    return;
  }

  const [[types], [groups], [transactions], [balance]] = results;

  state = controller.getState();

  if (state.BillingWalletV4Sequence?.patientId !== params.patientId) {
    return;
  }

  controller.setState({
    BillingWalletV4Sequence: {
      ...state.BillingWalletV4Sequence,
      types: types?.items ?? [],
      groups: groups?.items ?? [],
      transactions: transactions?.items ?? [],
      total: String(balance?.balance ?? "0.00").replace(/,/g, ""),
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD]: BLC_STATUS.SUCCESS },
  });
};

/** เปิด pop-up ฝากเงิน: บัญชีรับ เครื่องรูด และเลขใบเสร็จถัดไป */
const HandleDepositOpen: Handler<Params<Actions["DEPOSIT_OPEN"]>> = async (controller, params) => {
  let state = controller.getState();
  const extra = { device: controller.data.device, division: controller.data.division };

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DEPOSIT_OPEN]: BLC_STATUS.LOADING },
    errorMessage: {
      ...state.errorMessage,
      [BTN_ACTS.DEPOSIT_OPEN]: null,
      [BTN_ACTS.DEPOSIT_SUBMIT]: null,
    },
  });

  controller.handleEvent({ message: "HandleGetReceiptCode", params: {} });

  const results: ApiResult[] = await Promise.all([
    MasterAccountList.list({ apiToken: controller.apiToken, extra }),
    MasterEDCList.list({ apiToken: controller.apiToken, extra }),
  ]);

  const error = results.find((result) => result[1])?.[1];

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.DEPOSIT_OPEN, error });

    return;
  }

  const [[accounts], [edcs]] = results;

  state = controller.getState();

  controller.setState({
    BillingWalletV4Sequence: {
      ...state.BillingWalletV4Sequence,
      deposit: {
        accounts: (accounts?.items ?? []).filter(
          (item: PayMaster & { active?: boolean }) => item.active,
        ),
        edcs: (edcs?.items ?? []).filter((item: PayMaster & { active?: boolean }) => item.active),
      },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DEPOSIT_OPEN]: BLC_STATUS.SUCCESS },
  });
};

/** บันทึกฝากเงิน (BIL/deposit/) แล้วโหลด Wallet ใหม่ */
const HandleDepositSubmit: Handler<Params<Actions["DEPOSIT_SUBMIT"]>> = async (
  controller,
  params,
) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DEPOSIT_SUBMIT]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.DEPOSIT_SUBMIT]: null },
  });

  // field ที่ SDK ไม่มี: code, receipt_code, wallet_type
  const data: Partial<DepositReceiptSerializer> & DepositData & Record<string, unknown> = {
    ...params.data,
    patient: state.BillingWalletV4Sequence?.patientId,
    invoice: null,
    code: "",
    receipt_code: state.bilReceiptCodeDetail?.code || "",
  };

  const [res, error]: ApiResult = await DepositView.create({
    apiToken: controller.apiToken,
    data,
    extra: { device: controller.data.device, division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.DEPOSIT_SUBMIT, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    BillingWalletV4Sequence: { ...state.BillingWalletV4Sequence, deposit: null },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DEPOSIT_SUBMIT]: BLC_STATUS.SUCCESS },
  });

  controller.handleEvent({ message: "HandleGetReceiptCode", params: {} });

  params.onDone?.(res);

  await reloadWallets(controller);
};

const HandleDepositClose: Handler<Params<Actions["DEPOSIT_CLOSE"]>> = async (controller) => {
  const state = controller.getState();

  controller.setState({
    BillingWalletV4Sequence: { ...state.BillingWalletV4Sequence, deposit: null },
  });
};

/** พิมพ์ใบรับเงินมัดจำ */
const HandleReceiptPrint: Handler<Params<Actions["RECEIPT_PRINT"]>> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.RECEIPT_PRINT]: BLC_STATUS.LOADING },
  });

  const { HandlePrintReceipt } = await import("react-lib/apps/HISV3/BIL/sequence/BillPayment");

  await HandlePrintReceipt(controller as unknown as Parameters<typeof HandlePrintReceipt>[0], {
    id: params.receipt.id,
    receiptType: "deposit",
    isDeposit: true,
  });

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.RECEIPT_PRINT]: BLC_STATUS.SUCCESS,
    },
  });
};

/** ยกเลิกและคืนเงินมัดจำ: คืนยอดคงเหลือทั้งหมดของการฝากครั้งนั้น (wallet-tx-group-cancel) */
const HandleRefund: Handler<Params<Actions["REFUND"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.REFUND]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.REFUND]: null },
  });

  const [, error]: ApiResult = await WalletTransactionGroupCancel.delete({
    apiToken: controller.apiToken,
    pk: params.group.id,
    data: params.data,
    extra: { device: controller.data.device, division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.REFUND, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.REFUND]: BLC_STATUS.SUCCESS,
    },
  });

  params.onDone?.();

  await reloadWallets(controller);
};

const reloadWallets = async (controller: Parameters<Handler>[0]) => {
  const patientId = controller.getState().BillingWalletV4Sequence?.patientId;

  if (!patientId) {
    return;
  }

  await HandleLoad(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.LOAD, patientId });
};
